import { TTSCacheService } from './ttsCache';

interface TTSRequest {
  text: string;
}

interface TTSResponse {
  audioContent: string;
}

export class TTSApiService {
  private static readonly API_URL = '/api';
  
  private static currentAudio: HTMLAudioElement | null = null;
  private static audioContext: AudioContext | null = null;
  private static currentEventHandlers: {
    canplaythrough?: () => void;
    error?: (e: Event) => void;
    ended?: () => void;
  } = {};

  /**
   * 텍스트를 음성으로 변환하여 재생합니다.
   */
  static async speakText(
    text: string, 
    isMaleVoice: boolean = false, 
    speakingRate: number = 1.0, 
    languageCode: string = 'en-US',
    bookCode: string = '',
    chapter: number = 0,
    verse: number = 0,
    bibleVersion: string = 'msg'
  ): Promise<void> {
    try {
      // 현재 재생 중인 음성 완전히 정지
      this.stopCurrentAudio();
      
      // 이전 오디오가 완전히 정리될 때까지 잠시 대기
      await new Promise(resolve => setTimeout(resolve, 50));

      // 캐시 키 생성 (성경 정보가 있는 경우)
      let cacheKey: string | null = null;
      if (bookCode && chapter > 0 && verse > 0) {
        const voiceName = languageCode === 'cmn-CN' 
          ? (isMaleVoice ? 'cmn-CN-Standard-B' : 'cmn-CN-Standard-A')
          : languageCode === 'ko-KR'
          ? (isMaleVoice ? 'ko-KR-Standard-C' : 'ko-KR-Standard-A')
          : (isMaleVoice ? 'en-US-Standard-D' : 'en-US-Standard-C');
        
        // speakingRate를 적절한 소수점 자리수로 반올림
        const roundedSpeakingRate = Math.round(speakingRate * 10) / 10;
        
        cacheKey = TTSCacheService.generateCacheKey(
          bibleVersion,
          bookCode,
          chapter,
          verse,
          voiceName,
          roundedSpeakingRate
        );

        // 캐시에서 오디오 데이터 조회
        const cachedAudio = await TTSCacheService.getAudioFromCache(cacheKey);
        if (cachedAudio) {
          console.log('캐시에서 오디오 재생:', cacheKey);
          await this.playAudioFromBuffer(cachedAudio);
          return;
        }
      }

      // AudioContext 초기화 (사용자 상호작용 후)
      if (!this.audioContext) {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      }

      // AudioContext가 suspended 상태라면 resume
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      // 언어별 음성 설정
      let voiceName: string;
      if (languageCode === 'cmn-CN') {
        // 중국어 (CUV)
        voiceName = isMaleVoice ? 'cmn-CN-Standard-B' : 'cmn-CN-Standard-A';
      } else if (languageCode === 'ja-JP') {
        // 일본어 (JNIV)
        voiceName = isMaleVoice ? 'ja-JP-Standard-C' : 'ja-JP-Standard-A';
      } else if (languageCode === 'ko-KR') {
        // 한국어 (NKRV)
        voiceName = isMaleVoice ? 'ko-KR-Standard-C' : 'ko-KR-Standard-A';
      } else {
        // 영어 (MSG, NIV)
        voiceName = isMaleVoice ? 'en-US-Standard-D' : 'en-US-Standard-C';
      }

      const requestData: TTSRequest = {
        text: text
      };

      console.log('TTS API 호출 중...', text.substring(0, 50) + '...');

      // speakingRate를 적절한 소수점 자리수로 반올림
      const roundedSpeakingRate = Math.round(speakingRate * 10) / 10;

      const response = await fetch(`${this.API_URL}/tts?languageCode=${languageCode}&voiceName=${voiceName}&speakingRate=${roundedSpeakingRate}`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        throw new Error(`TTS API error: ${response.status}`);
      }

      const data: TTSResponse = await response.json();
      console.log('TTS API 응답 받음, 오디오 데이터 길이:', data.audioContent.length);
      
      let audioUrl: string;
      
      try {
        // Base64 인코딩된 오디오 데이터를 디코딩
        const audioData = atob(data.audioContent);
        console.log('Base64 디코딩 완료, 바이너리 데이터 길이:', audioData.length);
        
        // Uint8Array로 변환
        const audioArray = new Uint8Array(audioData.length);
        for (let i = 0; i < audioData.length; i++) {
          audioArray[i] = audioData.charCodeAt(i);
        }
        
        const audioBlob = new Blob([audioArray], { type: 'audio/mp3' });
        audioUrl = URL.createObjectURL(audioBlob);

        console.log('오디오 URL 생성:', audioUrl);
        console.log('Blob 크기:', audioBlob.size, 'bytes');

        // 캐시에 저장 (성경 정보가 있는 경우)
        if (cacheKey) {
          await TTSCacheService.saveAudioToCache(cacheKey, audioArray.buffer);
          console.log('TTS 오디오 캐시 저장 완료:', cacheKey);
        }
      } catch (decodeError) {
        console.error('Base64 디코딩 에러:', decodeError);
        throw new Error('오디오 데이터 디코딩 실패');
      }

      // 오디오 재생
      const audio = new Audio();
      this.currentAudio = audio;
      
      // 볼륨 설정
      audio.volume = 1.0;
      
      // 이벤트 핸들러를 클래스 변수에 저장
      this.currentEventHandlers.canplaythrough = () => {
        console.log('Audio loaded, attempting to play...');
        audio.play().then(() => {
          console.log('Audio started playing');
        }).catch(error => {
          console.error('Audio play failed:', error);
          // 자동 재생이 차단된 경우 사용자에게 알림
          alert('오디오 재생이 차단되었습니다. 브라우저 설정을 확인해주세요.');
        });
      };

      this.currentEventHandlers.error = (e: Event) => {
        console.error('Audio load error:', e);
        console.error('Audio error details:', {
          error: audio.error,
          networkState: audio.networkState,
          readyState: audio.readyState
        });
      };

      this.currentEventHandlers.ended = () => {
        console.log('Audio playback ended');
        URL.revokeObjectURL(audioUrl);
        this.currentAudio = null;
        // 재생 완료 시 상태 업데이트를 위한 이벤트 발생
        window.dispatchEvent(new CustomEvent('ttsPlaybackEnded'));
      };

      // 이벤트 리스너 등록
      audio.addEventListener('canplaythrough', this.currentEventHandlers.canplaythrough);
      audio.addEventListener('error', this.currentEventHandlers.error);
      audio.addEventListener('ended', this.currentEventHandlers.ended);

      // 오디오 소스 설정 및 로드
      audio.src = audioUrl;
      audio.load();

    } catch (error) {
      console.error('TTS API 호출 중 오류가 발생했습니다:', error);
      throw error;
    }
  }

  /**
   * 캐시된 오디오 버퍼를 재생합니다.
   */
  private static async playAudioFromBuffer(audioBuffer: ArrayBuffer): Promise<void> {
    try {
      const audioBlob = new Blob([audioBuffer], { type: 'audio/mp3' });
      const audioUrl = URL.createObjectURL(audioBlob);
      
      const audio = new Audio();
      this.currentAudio = audio;
      audio.volume = 1.0;

      // 이벤트 핸들러를 클래스 변수에 저장
      this.currentEventHandlers.canplaythrough = () => {
        console.log('캐시된 오디오 로드 완료, 재생 시작');
        audio.play().then(() => {
          console.log('캐시된 오디오 재생 시작');
        }).catch(error => {
          console.error('캐시된 오디오 재생 실패:', error);
        });
      };

      this.currentEventHandlers.error = (e: Event) => {
        console.error('캐시된 오디오 로드 에러:', e);
        // 오류 발생 시 정리
        URL.revokeObjectURL(audioUrl);
        this.currentAudio = null;
      };

      this.currentEventHandlers.ended = () => {
        console.log('캐시된 오디오 재생 완료');
        URL.revokeObjectURL(audioUrl);
        this.currentAudio = null;
        window.dispatchEvent(new CustomEvent('ttsPlaybackEnded'));
      };

      // 이벤트 리스너 등록
      audio.addEventListener('canplaythrough', this.currentEventHandlers.canplaythrough);
      audio.addEventListener('error', this.currentEventHandlers.error);
      audio.addEventListener('ended', this.currentEventHandlers.ended);

      // 오디오 소스 설정
      audio.src = audioUrl;
      audio.load();
    } catch (error) {
      console.error('캐시된 오디오 재생 중 오류:', error);
      throw error;
    }
  }

  /**
   * 현재 재생 중인 음성을 정지합니다.
   */
  static stopCurrentAudio(): void {
    if (this.currentAudio) {
      console.log('Stopping current audio...');
      
      try {
        // 저장된 이벤트 핸들러 제거
        if (this.currentEventHandlers.canplaythrough) {
          this.currentAudio.removeEventListener('canplaythrough', this.currentEventHandlers.canplaythrough);
        }
        if (this.currentEventHandlers.error) {
          this.currentAudio.removeEventListener('error', this.currentEventHandlers.error);
        }
        if (this.currentEventHandlers.ended) {
          this.currentAudio.removeEventListener('ended', this.currentEventHandlers.ended);
        }
        
        // 오디오 정지
        this.currentAudio.pause();
        this.currentAudio.currentTime = 0;
        
        // URL 정리 (blob URL인 경우)
        if (this.currentAudio.src && this.currentAudio.src.startsWith('blob:')) {
          URL.revokeObjectURL(this.currentAudio.src);
        }
        
        // 소스 제거
        this.currentAudio.src = '';
        this.currentAudio.load();
        
      } catch (error) {
        console.error('오디오 정지 중 오류:', error);
      } finally {
        // 이벤트 핸들러 참조 제거
        this.currentEventHandlers = {};
        // 현재 오디오 참조 제거
        this.currentAudio = null;
      }
    }
  }

  /**
   * 현재 재생 중인 음성이 있는지 확인합니다.
   */
  static isPlaying(): boolean {
    return this.currentAudio !== null && !this.currentAudio.paused;
  }
}
